import { Link, useParams } from '@tanstack/react-router';
import { Check, Download, ImagePlus, Mail, RefreshCw, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CHANNEL_LABELS, type ReviewAction } from '@marketingbee/contracts';
import { CHANNEL_RULES, composeBody, isEditable, platformLength } from '@marketingbee/domain/marketing';
import { Alert, Button, Card, CardContent, Dialog, DialogContent, Field, Input, PageHeader, Textarea, cn, formatDateTime, useToast } from '@marketingbee/ui';
import { QueryView, Section } from '../components/common';
import { audienceName, ChannelBadge, formatInZone, fromZonedInput, IssueList, pillarName, StatusPill, STATUS_LABELS, toZonedInput, useCanManageContent } from '../components/marketing';
import { errorMessage, trpc, type RouterOutputs } from '../lib/trpc';

type Item = RouterOutputs['content']['get'];

const MEDIA_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
type MediaType = (typeof MEDIA_TYPES)[number];
const MAX_MEDIA_BYTES = 1_000_000;

const ACTION_LABELS: Partial<Record<ReviewAction, string>> = {
  submit: 'Send for review',
  approve: 'Approve',
  request_changes: 'Request changes',
  reject: 'Reject',
  schedule: 'Schedule',
  unschedule: 'Unschedule',
  retry: 'Retry publishing',
  archive: 'Archive',
};
const MANAGER_ACTIONS: ReviewAction[] = ['approve', 'request_changes', 'reject', 'schedule', 'unschedule', 'retry', 'archive'];
const NEEDS_COMMENT: ReviewAction[] = ['request_changes', 'reject'];

export function ContentEditorPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const item = trpc.content.get.useQuery({ id });
  return (
    <QueryView query={item} label="Loading the post">
      {(data) => <Editor key={`${data.id}-${data.version}`} item={data} />}
    </QueryView>
  );
}

function Editor({ item }: { item: Item }) {
  const brand = trpc.brand.get.useQuery({ id: item.brandId });
  const issues = trpc.content.check.useQuery({ id: item.id });
  const tz = brand.data?.profile.timezone ?? 'America/Toronto';
  const canManage = useCanManageContent();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  const update = trpc.content.update.useMutation();
  const action = trpc.content.action.useMutation();
  const redraft = trpc.content.redraft.useMutation();
  const upload = trpc.content.uploadMedia.useMutation();
  const sendTest = trpc.email.sendTest.useMutation();
  const rules = CHANNEL_RULES[item.channel];
  const editable = isEditable(item.status);

  const [title, setTitle] = useState(item.title ?? '');
  const [body, setBody] = useState(item.body);
  const [hashtags, setHashtags] = useState(item.hashtags.join(' '));
  const [when, setWhen] = useState('');
  const [comment, setComment] = useState<{ action: ReviewAction; text: string } | null>(null);
  const [direction, setDirection] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (brand.data) setWhen(toZonedInput(item.scheduledAt, brand.data.profile.timezone));
  }, [brand.data, item.scheduledAt]);

  const tags = hashtags.split(/[\s,]+/).map((t) => t.trim().replace(/^#/, '')).filter(Boolean);
  const composed = composeBody(item.channel, { body, hashtags: tags });
  const length = platformLength(item.channel, composed);
  const over = length > rules.maxLength;
  const original = toZonedInput(item.scheduledAt, tz);
  const dirty = title !== (item.title ?? '') || body !== item.body || tags.join(' ') !== item.hashtags.join(' ') || (when !== original && !!brand.data);

  const refresh = async () => {
    await Promise.all([utils.content.get.invalidate({ id: item.id }), utils.content.check.invalidate({ id: item.id }), utils.content.list.invalidate(), utils.dashboard.summary.invalidate()]);
  };

  const save = () => {
    setSaveError(null);
    const scheduledAt = when ? fromZonedInput(when, tz) : null;
    update.mutate(
      {
        contentId: item.id,
        ...(title !== (item.title ?? '') ? { title: title || undefined } : {}),
        ...(body !== item.body ? { body } : {}),
        ...(tags.join(' ') !== item.hashtags.join(' ') ? { hashtags: tags } : {}),
        ...(when !== original ? { scheduledAt } : {}),
      },
      {
        onSuccess: async (r) => {
          success(r.status !== item.status ? `Saved: now ${STATUS_LABELS[r.status].toLowerCase()}` : 'Saved');
          await refresh();
        },
        onError: (e) => setSaveError(errorMessage(e)),
      },
    );
  };

  const run = (a: ReviewAction, text?: string) =>
    action.mutate(
      { contentId: item.id, action: a, comment: text || undefined },
      {
        onSuccess: async (r) => {
          success(`${ACTION_LABELS[a] ?? a}: done`, r.status === 'scheduled' && a === 'approve' ? 'It is scheduled.' : undefined);
          setComment(null);
          await refresh();
        },
        onError: (e) => error('Could not do that', errorMessage(e)),
      },
    );

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    if (!(MEDIA_TYPES as readonly string[]).includes(file.type)) return error('Use a PNG, JPEG, WebP or GIF image');
    if (file.size > MAX_MEDIA_BYTES) return error('Images can be up to 1 MB');
    try {
      const dataBase64 = await readBase64(file);
      const media = await upload.mutateAsync({ name: file.name.slice(0, 200), mimeType: file.type as MediaType, dataBase64, brandId: item.brandId });
      await update.mutateAsync({ contentId: item.id, mediaIds: [...item.mediaIds, media.id] });
      success('Image attached');
      await refresh();
    } catch (e) {
      error('Could not upload the image', errorMessage(e));
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const actions = (item.actions as string[]).filter((a): a is ReviewAction => a in ACTION_LABELS).filter((a) => canManage || !MANAGER_ACTIONS.includes(a));
  const liveIssues = issues.data ?? item.issues;
  const profile = brand.data?.profile;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={
          <>
            <Link to="/calendar" className="hover:underline">Calendar</Link> · <Link to="/review" className="hover:underline">Review</Link>
          </>
        }
        title={item.title || `${CHANNEL_LABELS[item.channel]} post`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <ChannelBadge channel={item.channel} />
            <StatusPill status={item.status} />
            <span>{formatInZone(item.scheduledAt, tz)}</span>
            {pillarName(profile, item.pillarId) ? <span>· {pillarName(profile, item.pillarId)}</span> : null}
            {audienceName(profile, item.audienceId) ? <span>· for {audienceName(profile, item.audienceId)}</span> : null}
          </span>
        }
      />

      {item.status === 'approved' || item.status === 'scheduled' ? (
        <Alert variant="info">Editing the text, hashtags or image of approved or scheduled content sends it back for approval. Changing only the time keeps it scheduled.</Alert>
      ) : null}
      {item.status === 'failed' && item.lastError ? <Alert variant="destructive" title="Publishing failed">{item.lastError}</Alert> : null}
      {item.status === 'published' && item.externalUrl ? (
        <Alert variant="success" title="Published">
          <a className="underline" href={item.externalUrl} target="_blank" rel="noreferrer">View the post</a>
        </Alert>
      ) : null}
      {item.reviewComment && item.status === 'changes_requested' ? <Alert variant="warning" title="Changes requested">{item.reviewComment}</Alert> : null}
      {item.humanOnly ? <Alert variant="info" title="Write this one yourself">This pillar is written by a person. MarketingBee never drafts it, and it can only be approved once the text is your own.</Alert> : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardContent className="space-y-4 py-6">
              {saveError ? <Alert variant="destructive">{saveError}</Alert> : null}
              {rules.needsTitle || item.title ? (
                <Field label={item.channel === 'email' ? 'Subject line' : 'Title'} required={rules.needsTitle}>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} disabled={!editable} />
                </Field>
              ) : null}
              <Field label="Text">
                <Textarea rows={item.channel === 'blog' || item.channel === 'email' ? 16 : 8} value={body} onChange={(e) => setBody(e.target.value)} disabled={!editable} aria-describedby="char-count" />
              </Field>
              <p id="char-count" className={cn('text-right font-mono text-[12px]', over ? 'text-destructive' : 'text-muted-foreground')}>
                {length.toLocaleString('en-CA')} / {rules.maxLength.toLocaleString('en-CA')} characters{rules.maxHashtags === 0 ? '' : ' (with hashtags)'}
                {length < rules.idealLength[0] ? ` · aim for at least ${rules.idealLength[0]}` : length > rules.idealLength[1] && !over ? ` · longer than the usual ${rules.idealLength[1]}` : ''}
              </p>
              {rules.maxHashtags !== 0 ? (
                <Field label="Hashtags" description={rules.maxHashtags ? `Up to ${rules.maxHashtags}. Separate with spaces.` : 'Separate with spaces.'}>
                  <Input value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="#smallbusiness #toronto" disabled={!editable} />
                </Field>
              ) : null}
              <Field label={`Publish at (${tz.replace(/_/g, ' ')})`}>
                <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} disabled={!editable} className="max-w-64" />
              </Field>
              {!rules.clickableLinks ? <p className="text-[13px] text-muted-foreground">Links are not clickable on {rules.label}: point people to the link in your bio.</p> : null}
              {editable ? (
                <div className="flex flex-wrap gap-2">
                  <Button disabled={!dirty} loading={update.isPending} onClick={save}>
                    Save
                  </Button>
                  {!item.humanOnly ? (
                    <Button variant="outline" onClick={() => setDirection('')}>
                      <RefreshCw /> Redraft with direction
                    </Button>
                  ) : null}
                </div>
              ) : (
                <p className="text-[13px] text-muted-foreground">Content that is {STATUS_LABELS[item.status].toLowerCase()} cannot be edited.</p>
              )}
            </CardContent>
          </Card>

          <Section title="Image" description={rules.requiresMedia ? `${rules.label} needs an image or video.` : 'Optional. PNG, JPEG, WebP or GIF up to 1 MB.'}>
            <Card>
              <CardContent className="space-y-3 py-5">
                {item.imageIdea ? <p className="text-[13px] text-muted-foreground"><span className="font-medium text-foreground">Image idea:</span> {item.imageIdea}</p> : null}
                <div className="flex flex-wrap gap-3">
                  {item.media.map((m) => (
                    <MediaThumb key={m.id} id={m.id} name={m.name} onRemove={editable ? () => update.mutate({ contentId: item.id, mediaIds: item.mediaIds.filter((x) => x !== m.id) }, { onSuccess: () => void refresh(), onError: (e) => error('Could not remove', errorMessage(e)) }) : undefined} />
                  ))}
                  {item.media.length === 0 ? <p className="text-[13px] text-muted-foreground">No image attached.</p> : null}
                </div>
                {editable ? (
                  <>
                    <input ref={fileRef} type="file" accept={MEDIA_TYPES.join(',')} className="sr-only" id="media-upload" onChange={(e) => void onFile(e.target.files?.[0])} />
                    <Button variant="outline" size="sm" loading={upload.isPending} onClick={() => fileRef.current?.click()}>
                      <ImagePlus /> Upload image
                    </Button>
                  </>
                ) : null}
              </CardContent>
            </Card>
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Checks">
            <Card>
              <CardContent className="space-y-3 py-5">
                <IssueList issues={liveIssues} empty="No issues found against your guardrails." />
                {dirty ? <p className="text-[12px] text-muted-foreground">Save to check your changes.</p> : null}
              </CardContent>
            </Card>
          </Section>

          {actions.length ? (
            <Section title="Actions">
              <Card>
                <CardContent className="flex flex-wrap gap-2 py-5">
                  {actions.map((a) => (
                    <Button
                      key={a}
                      size="sm"
                      variant={a === 'approve' || a === 'schedule' || a === 'submit' ? 'primary' : a === 'reject' ? 'ghost' : 'outline'}
                      disabled={dirty && (a === 'approve' || a === 'submit')}
                      title={dirty && (a === 'approve' || a === 'submit') ? 'Save your changes first' : undefined}
                      loading={action.isPending && action.variables?.action === a}
                      onClick={() => (NEEDS_COMMENT.includes(a) ? setComment({ action: a, text: '' }) : run(a))}
                    >
                      {a === 'approve' ? <Check /> : a === 'reject' ? <X /> : null}
                      {ACTION_LABELS[a]}
                    </Button>
                  ))}
                </CardContent>
              </Card>
            </Section>
          ) : null}

          {item.channel === 'blog' ? (
            <Section title="Blog post">
              <Card>
                <CardContent className="space-y-3 py-5 text-[14px]">
                  <p className="text-muted-foreground">Blog posts are not posted automatically. Download the Markdown and paste it into your site, then mark it published by scheduling it.</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      void utils.content.exportMarkdown.fetch({ id: item.id }).then(
                        (r) => {
                          const url = URL.createObjectURL(new Blob([r.markdown], { type: 'text/markdown' }));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = r.filename;
                          a.click();
                          URL.revokeObjectURL(url);
                        },
                        (e: unknown) => error('Could not export', errorMessage(e)),
                      )
                    }
                  >
                    <Download /> Download Markdown
                  </Button>
                </CardContent>
              </Card>
            </Section>
          ) : null}

          {item.channel === 'email' ? (
            <Section title="Email">
              <Card>
                <CardContent className="space-y-3 py-5 text-[14px]">
                  <p className="text-muted-foreground">Send this email to yourself to see it as subscribers will, footer included. Tests are not counted or tracked.</p>
                  <Button size="sm" variant="outline" loading={sendTest.isPending} onClick={() => sendTest.mutate({ id: item.id }, { onSuccess: (r) => success('Test sent', `Check ${r.sentTo}.`), onError: (e) => error('Could not send the test', errorMessage(e)) })}>
                    <Mail /> Send me a test
                  </Button>
                  {Object.keys(item.sends).length ? (
                    <ul className="text-[13px] text-muted-foreground">
                      {Object.entries(item.sends).map(([status, n]) => (
                        <li key={status}>
                          {status.replace(/_/g, ' ')}: <span className="font-mono">{n}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </CardContent>
              </Card>
            </Section>
          ) : null}

          <Section title="Tracked links">
            <Card>
              <CardContent className="divide-y divide-border py-2 text-[13px]">
                {item.links.length === 0 ? <p className="py-3 text-muted-foreground">Links are tracked when the post is published.</p> : null}
                {item.links.map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-3 py-2.5">
                    <span className="min-w-0 truncate" title={l.destination}>{l.destination}</span>
                    <span className="shrink-0 font-mono">{l.clicks} clicks</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </Section>

          <Section title="History">
            <Card>
              <CardContent className="space-y-3 py-5">
                {item.events.length === 0 ? <p className="text-[13px] text-muted-foreground">No history yet.</p> : null}
                {item.events.map((e) => (
                  <div key={e.id} className="border-l-2 border-border pl-3 text-[13px]">
                    <p className="font-medium">
                      {e.action.replace(/_/g, ' ')}
                      {e.toStatus ? <span className="font-normal text-muted-foreground"> → {STATUS_LABELS[e.toStatus as keyof typeof STATUS_LABELS] ?? e.toStatus}</span> : null}
                    </p>
                    {e.comment ? <p className="text-muted-foreground">{e.comment}</p> : null}
                    <p className="text-[12px] text-muted-foreground">
                      {formatDateTime(e.createdAt)} · {e.actorType === 'user' ? (e.actorName ?? 'a teammate') : e.actorType === 'digest' ? `${e.actorName ?? 'A reviewer'}, from the weekly email` : 'MarketingBee'}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </Section>
        </div>
      </div>

      <Dialog open={comment !== null} onOpenChange={(v) => !v && setComment(null)}>
        <DialogContent
          title={comment?.action === 'reject' ? 'Reject this post' : 'Request changes'}
          footer={
            <>
              <Button variant="outline" onClick={() => setComment(null)}>Cancel</Button>
              <Button variant={comment?.action === 'reject' ? 'destructive' : 'primary'} disabled={!comment?.text.trim()} loading={action.isPending} onClick={() => comment && run(comment.action, comment.text)}>
                {comment ? ACTION_LABELS[comment.action] : ''}
              </Button>
            </>
          }
        >
          <Field label="Comment">
            <Textarea rows={3} value={comment?.text ?? ''} maxLength={2000} onChange={(e) => setComment((c) => (c ? { ...c, text: e.target.value } : c))} />
          </Field>
        </DialogContent>
      </Dialog>

      <Dialog open={direction !== null} onOpenChange={(v) => !v && setDirection(null)}>
        <DialogContent
          title="Redraft with direction"
          description="The writer replaces the current text with a new version. Copy anything you want to keep first."
          footer={
            <>
              <Button variant="outline" onClick={() => setDirection(null)}>Cancel</Button>
              <Button
                loading={redraft.isPending}
                onClick={() =>
                  redraft.mutate(
                    { contentId: item.id, direction: direction || undefined },
                    {
                      onSuccess: async () => {
                        success('New draft ready');
                        setDirection(null);
                        await refresh();
                      },
                      onError: (e) => error('Could not redraft', errorMessage(e)),
                    },
                  )
                }
              >
                Redraft
              </Button>
            </>
          }
        >
          <Field label="Direction (optional)">
            <Input value={direction ?? ''} maxLength={500} onChange={(e) => setDirection(e.target.value)} placeholder="Shorter, friendlier, lead with the payment problem" />
          </Field>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MediaThumb({ id, name, onRemove }: { id: string; name: string; onRemove?: () => void }) {
  const media = trpc.content.media.useQuery({ id }, { staleTime: Infinity });
  return (
    <figure className="relative w-32 overflow-hidden rounded-lg bg-muted">
      {media.data ? <img src={media.data.dataUrl} alt={media.data.alt ?? name} className="aspect-square w-full object-cover" /> : <div className="aspect-square w-full animate-pulse" />}
      <figcaption className="truncate px-2 py-1 text-[11px] text-muted-foreground">{name}</figcaption>
      {onRemove ? (
        <Button size="icon" variant="secondary" className="absolute right-1 top-1 size-7" aria-label={`Remove ${name}`} onClick={onRemove}>
          <Trash2 />
        </Button>
      ) : null}
    </figure>
  );
}

/** File → base64 without the `data:...;base64,` prefix. */
function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}
